import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpErrorResponse } from '@angular/common/http';
import { EventEmitter, Injectable, Output } from '@angular/core';
import { WebApiService } from 'src/app/shared/services/web-api.service';
import { Page } from 'src/app/shared/models/page';
import { Observable, from, Subject } from 'rxjs';
import { PagedData } from 'src/app/shared/models/paged-data';
import { Supplier, SupplierUpdateResult, SupplierValidationResult } from 'src/app/shared/models/supplier.model';
import { HttpParams } from '@angular/common/http';
import { PageParams } from 'src/app/shared/common/Enums';
import { catchError, map, tap } from 'rxjs/operators';
import { ApiUrlConstants } from "src/app/shared/common/ApiUrlConstants";
import { SupplierProduct } from 'src/app/shared/models/supplier-product.model';
import { RfqTokenAuthService } from 'src/app/shared/services/rfq-token-auth.service';
import { ExistingSupplierMatch } from 'src/app/shared/models/existing-supplier-match.model';

@Injectable({
  providedIn: 'root'
})
export class SupplierService {
  @Output() supplierProductRecord: any = new EventEmitter();
  private supplierAddedSubject = new Subject<any>();
  supplierAdded$ = this.supplierAddedSubject.asObservable();

  constructor(
    private webApiService: WebApiService,
    private snack: MatSnackBar,
    private rfqTokenAuth: RfqTokenAuthService
  ) { }

  /**
   * Get all suppliers list with paginated options.
   * @param pageMetadata Page Metadata i.e. PAGE OFFSET, PAGE SIZE, TOTAL PAGES etc..
   */
  getPaginatedSuppliers(pageMetadata: Page): Observable<PagedData<Supplier>> {
    let apiUrl = "/supplier/";

    /**
     * Set request params for pagination variables.
     * @pageNumber Add +1 in PageNumber as page start with 0 in frontend data-table.
     */
    let params = new HttpParams()
      .set(PageParams.limit, pageMetadata.size)
      .set(PageParams.page, pageMetadata.pageNumber + 1)
      .set(PageParams.searchParam, pageMetadata.serachParam)
      .set(PageParams.isDelete, pageMetadata.isDelete)
      .set(PageParams.sortColoumn, pageMetadata.sortColoumn)
      .set(PageParams.sortOrder, pageMetadata.sortOrder);

    return this.webApiService
      .getPaginatedRequest<PagedData<Supplier>>(apiUrl, params)
      .pipe(
        map((paginatedSupplier) => {
          // Set page metadata for frontend table properties
          paginatedSupplier.page.totalElements = paginatedSupplier.count;
          paginatedSupplier.page.size = pageMetadata.size;
          paginatedSupplier.page.totalPages = Math.floor(
            paginatedSupplier.count / pageMetadata.size
          );
          paginatedSupplier.page.pageNumber = pageMetadata.pageNumber;
          paginatedSupplier.page.serachParam = pageMetadata.serachParam;
          return paginatedSupplier;
        })
      );
  }

  /** Notify listeners that a supplier was added (client-side list update) */
  announceSupplierAdded(payload: any) {
    try { this.supplierAddedSubject.next(payload); } catch {}
  }

  /**
   * Get all suppliers list.
   */
  getAllSuppliers(): Promise<Supplier[]> {
    let apiUrl = "/supplier/";
    return this.webApiService.getRequest<Supplier[]>(apiUrl);
  }

  /**
   * Get supplier detail by supplier id.
   * @param _supplierId Supplier Id.
   */
  getSupplierById(_supplierId: number): Promise<Supplier> {
    let apiUrl = `/supplier/${_supplierId}`;
    return this.webApiService.getRequest<Supplier>(apiUrl);
  }

  /**
   * Add new supplier.
   * @param _supplierDetail Supplier form data.
   */
  addSupplier(_supplierDetail: Supplier, supplierId?: any,buyerId?:number): Promise<any> {
    let apiUrl = `/supplier/add`;
    return this.webApiService.postRequest<any>(apiUrl, { ..._supplierDetail, supplierId, buyerId });
  }

  /**
   * Likely existing MPS suppliers for what the buyer is about to create (2026-10-20). Never blocks: on any error the
   * answer is [] and the create path's own check (HTTP 409) still applies.
   */
  findExistingSupplierMatches(email: string, companyName: string): Promise<ExistingSupplierMatch[]> {
    return this.webApiService
      .postRequest<any>(`/supplier/existingSupplierMatches`, { email: email || '', companyName: companyName || '' })
      .then((data: any) => (Array.isArray(data?.matches) ? data.matches : []))
      .catch(() => []);
  }

  /**
   * Which of these supplier ids the session may edit (admin: all; buyer: the ones it created; supplier: itself).
   * 2026-10-20. On any error the answer is [] (read-only is the safe default; the API refuses the edit anyway).
   */
  editableSupplierIds(ids: number[]): Promise<number[]> {
    return this.webApiService
      .postRequest<any>(`/supplier/editableSupplierIds`, { ids: (ids || []).map(Number) })
      .then((data: any) => (Array.isArray(data?.ids) ? data.ids.map(Number) : []))
      .catch(() => []);
  }

  addBuyerMySupplier(_supplierDetail: Supplier, supplierId?: any,buyerId?:number): Promise<any> {
    let apiUrl = `/supplier/addBuyerMySupplier`;
    return this.webApiService.postRequest<any>(apiUrl, { ..._supplierDetail, supplierId, buyerId });
  }

  importSuppliersByAdmin(reqBody: any): Promise<any> {
    let apiUrl = `/supplier/importSuppliersByAdmin`;
    return this.webApiService.postRequest<any>(apiUrl, reqBody);
  }

 
  importSuppliersByBuyer(reqBody: any): Promise<any> {
    const apiUrl = `/supplier/importSuppliersByBuyer`;
    return this.webApiService.postRequest<any>(apiUrl, reqBody);
  }

  importMySuppliersByBuyer(reqBody: any): Promise<any> {
    const apiUrl = `/supplier/importMySuppliersByBuyer`;
    return this.webApiService.postRequest<any>(apiUrl, reqBody);
  }
 
  addSupplierByBuyer(_supplierDetail: Supplier, supplierId?: any): Promise<any> {
    let apiUrl = `/supplier/addSupplierByBuyer`;
    return this.webApiService.postRequest<any>(apiUrl, { ..._supplierDetail, supplierId });
  }

  addSupplierByBuyerForServiceRfq(_supplierDetail: Supplier, supplierId?: any): Promise<any> {
    let apiUrl = `/services/rfq/addSupplierByBuyerForServiceRfq`;
    return this.webApiService.postRequest<any>(apiUrl, { ..._supplierDetail, supplierId });
  }

  /**
   * Update any existing supplier details by supplier id.
   * @param _supplierId To be updated supplier id.
   * @param _supplierDetail Supplier form data.
   */
  updateSupplier(_supplierId: number, _supplierDetail: Supplier): Promise<Supplier> {
    let apiUrl = `/supplier/add`;
    return this.webApiService.postRequest<Supplier>(apiUrl, {..._supplierDetail,_supplierId})
  };
  

  /**
 * Update any existing supplier details by supplier id.
 * @param _supplierId To be updated supplier id.
 * @param _supplierDetail Supplier form data.
 */
  validateSupplierName(emailAddressArr: string[]): Promise<SupplierValidationResult> {
    let apiUrl = `/supplier/validate`;
    const emailAddresses = { emailAddressArr };
    return this.webApiService.postRequest<SupplierValidationResult>(apiUrl, emailAddresses);
  }

  /**
* Update any existing supplier details by supplier id.
* @param _supplierId To be updated supplier id.
* @param _supplierDetail Supplier form data.
*/
  getSupplierUpdateDataByUserId(_userId: any): Promise<SupplierUpdateResult> {
    let apiUrl = `/supplier/fetch/userid/${_userId}`;
    return this.webApiService.getRequest<SupplierUpdateResult>(apiUrl);
  }

  getSupplierUpdateDataByUserIdByRfqToken(): Promise<SupplierUpdateResult> {
    let apiUrl = `/supplier/fetch/useridByRfqToken`;
    const options = this.rfqTokenAuth.buildAuthHeaders();
    return this.webApiService.getRequestWithOptions<SupplierUpdateResult>(apiUrl, options);
  }

  getSearchResult(searchTerms: any): Promise<any> {
    let apiUrl = `/supplier/search/${searchTerms}`;
    return this.webApiService.getRequest<any>(apiUrl);
  }

  /**
   * Remove any existing supplier by supplier id.
   * @param _supplierId To be removed supplier id.
   */
  deleteSupplier(_supplierId: number): Promise<Supplier> {
    let apiUrl = `/supplier/${_supplierId}`;
    return this.webApiService.deleteRequest<Supplier>(apiUrl);
  }

  //Restore
  restoreSupplier(_supplierId: number): Promise<Supplier> {
    let apiUrl = `/supplier/restore/${_supplierId}`;
    return this.webApiService.putRequest<Supplier>(apiUrl, { _supplierId: _supplierId });
  }

  /**
   * Get all suppliers list with paginated options.
   * @param pageMetadata Page Metadata i.e. PAGE OFFSET, PAGE SIZE, TOTAL PAGES etc..
   */
  getPaginatedSupplierProducts(pageMetadata: Page, supplierId: number): Observable<PagedData<SupplierProduct[]>> {
    let apiUrl = `/supplier/getSupplierProductDetails/${supplierId}`;

    /**
     * Set request params for pagination variables.
     * @pageNumber Add +1 in PageNumber as page start with 0 in frontend data-table.
     */
    let params = new HttpParams()
      .set(PageParams.limit, pageMetadata.size)
      .set(PageParams.page, pageMetadata.pageNumber + 1)
      .set(PageParams.searchParam, pageMetadata.serachParam)
      .set(PageParams.sortColoumn, pageMetadata.sortColoumn)
      .set(PageParams.sortOrder, pageMetadata.sortOrder);

    return this.webApiService
      .getPaginatedRequest<PagedData<SupplierProduct[]>>(apiUrl, params)
      .pipe(
        map((paginatedSupplierShipPOs) => {
          // Set page metadata for frontend table properties
          paginatedSupplierShipPOs.page.totalElements = paginatedSupplierShipPOs.count;
          paginatedSupplierShipPOs.page.size = pageMetadata.size;
          paginatedSupplierShipPOs.page.totalPages = Math.floor(
            paginatedSupplierShipPOs.count / pageMetadata.size
          );
          paginatedSupplierShipPOs.page.pageNumber = pageMetadata.pageNumber;
          paginatedSupplierShipPOs.page.serachParam = pageMetadata.serachParam;
          return paginatedSupplierShipPOs;
        })
      );
  }


  /**
   * Get supplier product detail by supplier product id.
   * @param _supplierProductId Supplier Product Id.
   */
  getSupplierProductById(_supplierProductId: number): Promise<SupplierProduct> {
    let apiUrl = `/supplier/supplierProduct/${_supplierProductId}`;
    return this.webApiService.getRequest<SupplierProduct>(apiUrl);
  }

  /**
   * Update any existing supplier product details by supplier product id.
   * @param _supplierProductId To be updated supplier product id.
   * @param _supplierProductDetail Supplier product form data.
   */
  updateSupplierProduct(_supplierProductId: number, _supplierProductDetail: SupplierProduct): Promise<SupplierProduct> {
    let apiUrl = `/supplier/supplierProduct/${_supplierProductId}`;
    return this.webApiService.putRequest<SupplierProduct>(apiUrl, _supplierProductDetail);
  }

  //#region Data transfer getter/setter.
  public setSupplierProduct(value: any): void {
    this.supplierProductRecord.next(value);
  }

  public getSupplierProduct(): Observable<any> {
    return this.supplierProductRecord;
  }

  // getSupplierData():Promise<any>{
  //   let apiUrl = `${ApiUrlConstants.getSupplierData}`
  //   return this.webApiService.getRequest<any>(apiUrl)
  // }

     // Fetch supplier data with limit, offset, and optional search parameter
     getSupplierData(limit: number, offset: number, searchParam: string = ''): Observable<{ rows: any[], totalElements: number }> {
      const apiUrl = `/settings/supplier/data?limit=${limit}&offset=${offset}&searchParam=${encodeURIComponent(searchParam)}`;
      return from(this.webApiService.getRequest1<{  rows: any[], totalElements: number }>(apiUrl))
      
    }

    getSupplierDataByBuyerId(limit: number, offset: number, searchParam: string = '', userId: number): Observable<{ rows: any[], totalElements: number }> {
      const apiUrl = `/settings/supplierByBuyerId/data?limit=${limit}&offset=${offset}&searchParam=${encodeURIComponent(searchParam)}&userId=${userId}`;
      console.log({apiUrl});
      return from(this.webApiService.getRequest1<{  rows: any[], totalElements: number }>(apiUrl))
      
    }

  /** Buyer My Suppliers KPI strip — Active / Verified / Pending counts computed
   *  server-side, so the page no longer fetches the whole roster just to count. */
  getMySupplierKpiCounts(userId: number): Promise<{ active: number; verified: number; pendingVerification: number }> {
    const apiUrl = `/settings/mySupplierKpiCounts?userId=${userId}`;
    return this.webApiService.getRequest1<{ active: number; verified: number; pendingVerification: number }>(apiUrl);
  }

  exportAllSuppliersByAdmin(): Observable<{ rows: any[]}> {
    let apiUrl = `/supplier/exportAllSuppliersByAdmin`;
    return from( this.webApiService.getRequest<{ rows: any[]}>(apiUrl));
  }

  /** Data Management summary-card counts for suppliers: total active accounts
   *  and, of those, how many are not yet KYC-verified. */
  getVerificationCounts(): Promise<{ activeCount: number; notVerifiedCount: number }> {
    const apiUrl = `/settings/userVerificationCounts?userCategory=1`;
    return this.webApiService.getRequest1<{ activeCount: number; notVerifiedCount: number }>(apiUrl);
  }

  /**
   * Verify PO magic link token.
   * Trims whitespace; expects raw JWT after any client-side prefix stripping.
   */
  verifyPoToken(token: string): Promise<any> {
    const apiUrl = `/supplier/verify-po-token`;
    return this.webApiService.postRequest<any>(apiUrl, { token: (token || '').trim() });
  }

  resendSupplierCredentials(userIds: number[]): Promise<any> {
    const apiUrl = `/supplier/resendCredentials`;
    return this.webApiService.postRequest<any>(apiUrl, { userIds });
  }

  /** Checks whether the selected supplier is valid or not */
  isValidSupplier(_selectedSupplier: Supplier) {
    return new Promise<Supplier | null>((resolve, reject) => {
      if (
        _selectedSupplier !== null &&
        _selectedSupplier !== undefined &&
        _selectedSupplier
      ) {
        this.getSupplierById(_selectedSupplier.id).then((dbSupplier) => {
          if (
            dbSupplier.id == _selectedSupplier.id &&
            dbSupplier.vsSupplierCode == _selectedSupplier.vsSupplierCode &&
            dbSupplier.companyName == _selectedSupplier.companyName
          ) {
            resolve(dbSupplier);
          } else {
            this.snack.open(`${"Selected Supplier Not Valid!"}`, "OK", {
              duration: 3000,
            });
          }
        }).catch((error: HttpErrorResponse) => {
          //console.log(error);
          this.snack.open(`${"Selected Supplier Not Valid!"}`, "OK", {
            duration: 3000,
          });
          reject("supplier rejected");
        });
      } else {
        resolve(null);
      }
    });
  }
}
